/**
 * Small, dependency-free colour toolkit for the brand theme engine (see buildTheme.ts).
 * Everything here is pure so it can be unit-tested without a DOM.
 */

/** sRGB triplet, each channel 0-255. */
export type RGB = readonly [number, number, number];

export const WHITE: RGB = [255, 255, 255];
export const BLACK: RGB = [0, 0, 0];

const HEX6 = /^#?([0-9a-f]{6})$/i;
const HEX3 = /^#?([0-9a-f]{3})$/i;

const clamp255 = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/** "#abc", "abc" or "#AABBCC" -> "#AABBCC". Anything else (partial input, NaN, non-strings) -> null. */
export function normalizeHex(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  const m6 = HEX6.exec(s);
  if (m6) return `#${m6[1].toUpperCase()}`;
  const m3 = HEX3.exec(s);
  if (m3) {
    return `#${m3[1]
      .split('')
      .map((c) => c + c)
      .join('')
      .toUpperCase()}`;
  }
  return null;
}

export function hexToRgb(hex: string): RGB {
  const n = normalizeHex(hex);
  if (!n) throw new Error(`Invalid hex colour: ${hex}`);
  return [parseInt(n.slice(1, 3), 16), parseInt(n.slice(3, 5), 16), parseInt(n.slice(5, 7), 16)];
}

export function rgbToHex(c: RGB): string {
  return `#${c.map((v) => clamp255(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** "R G B" channel string, the format Tailwind's `rgb(var(--x) / <alpha-value>)` colours expect. */
export function channels(c: RGB): string {
  return `${clamp255(c[0])} ${clamp255(c[1])} ${clamp255(c[2])}`;
}

/** Linear blend: t=0 -> a, t=1 -> b. */
export function mix(a: RGB, b: RGB, t: number): RGB {
  const k = clamp01(t);
  return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
}

/** WCAG relative luminance. */
export function luminance(c: RGB): number {
  const f = (v: number) => {
    const s = clamp255(v) / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrast(a: RGB, b: RGB): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** h in [0,360), s and l in [0,1]. */
export function rgbToHsl(c: RGB): [number, number, number] {
  const r = c[0] / 255;
  const g = c[1] / 255;
  const b = c[2] / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

export function hslToRgb(h: number, s: number, l: number): RGB {
  const hue = (((h % 360) + 360) % 360) / 360;
  if (s === 0) {
    const v = l * 255;
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [channel(hue + 1 / 3) * 255, channel(hue) * 255, channel(hue - 1 / 3) * 255];
}

/**
 * Smallest lightness shift of `fg` (hue and saturation preserved) that reaches `min` contrast against
 * EVERY background in `bgs`. Returns `fg` untouched when it already passes. When no shift can reach
 * the target the fully-shifted colour is returned (best effort, never throws).
 *
 * Lightness moves in 0.25% steps, which keeps derived colours visually close to the brand colour
 * they came from (e.g. #F77623 -> #C65207 rather than a different orange).
 */
export function ensureContrast(
  fg: RGB,
  bgs: RGB | readonly RGB[],
  min: number,
  direction: 'lighten' | 'darken',
): RGB {
  const list: readonly RGB[] = isRgbList(bgs) ? bgs : [bgs];
  const passes = (c: RGB) => list.every((bg) => contrast(c, bg) >= min);
  if (passes(fg)) return fg;

  const [h, s, l] = rgbToHsl(fg);
  const step = direction === 'lighten' ? 0.0025 : -0.0025;
  let last: RGB = fg;
  for (let i = 1; i <= 400; i += 1) {
    const nl = l + step * i;
    if (nl < 0 || nl > 1) break;
    last = hslToRgb(h, s, nl);
    if (passes(last)) return last;
  }
  return last;
}

function isRgbList(x: RGB | readonly RGB[]): x is readonly RGB[] {
  return Array.isArray(x) && Array.isArray((x as readonly unknown[])[0]);
}
